<template class="operators-status">
  <div>
    <v-dialog
      v-model="filterDialog"
      width="70vw"
      height="80vh"
    >
      <v-card>
        <v-card-actions class="operators-status__close-button">
          <DsButton
            v-tooltip="closeLabel"
            color="tertiary"
            name="close"
            :icon="true"
            @click="filterDialog = false"
          />
        </v-card-actions>
        <div class="operators-status__internal-style">
          <MultiSelectFilter
            v-model="selectedOperators"
            class="mr-3"
            :select-items="getOptions"
            item-value="id"
            :label="operatorsLabel"
            :searchable="true"
          />
          <MultiSelectFilter
            v-model="selectedStatus"
            class="mr-3"
            :select-items="status"
            :label="statusLabel"
            :searchable="true"
          />
          <div class="operators-status__buttons">
            <DsButton
              v-tooltip="filterLabel"
              class="operators-status__button"
              color="secondary"
              name="filter"
              :icon="true"
              @click="applyFilter"
            />
            <br>
            <DsButton
              v-tooltip="trashLabel"
              class="operators-status__button"
              color="secondary"
              name="trash"
              :icon="true"
              @click="resetFilter"
            />
          </div>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="dialog"
      width="70vw"
      height="80vh"
    >
      <v-card>
        <v-card-actions class="operators-status__close-button">
          <DsButton
            v-tooltip="closeLabel"
            color="tertiary"
            name="close"
            :icon="true"
            @click="dialog = false"
          />
        </v-card-actions>
        <v-card-text class="operators-status__modal-style">
          <h3>{{ this.$t('components.StatusTable.faq[0].title') }}</h3>
          <p>{{ this.$t('components.StatusTable.faq[0].description') }}</p>

          <h3>{{ this.$t('components.StatusTable.faq[1].title') }}</h3>
          <p>{{ this.$t('components.StatusTable.faq[1].description') }}</p>

          <h3>{{ this.$t('components.StatusTable.faq[2].title') }}</h3>
          <p>{{ this.$t('components.StatusTable.faq[2].description') }}</p>

          <h3>{{ this.$t('components.StatusTable.faq[3].title') }}</h3>
          <p>{{ this.$t('components.StatusTable.faq[3].description') }}</p>
        </v-card-text>
      </v-card>
    </v-dialog>
    <GenericChart
      :request-status="requestStatus"
      :title="title"
      :export-xlsx="getExportData"
      :open-dialog="openDialog"
      :open-filter="openFilter"
      :item-id="itemId"
      :has-filter="hasFilter"
      @reload="getData"
    />
    <v-data-table
      :v-if="requestStatus=='success'"
      :headers="headers"
      :items="items"
      :loading="inputLoading"
      :options="options"
      :footer-props="footerProps"
      class="operators-status__table-truncate"
    >
      <template v-slot:item.userId="{ item }">
        <DsButton
          v-tooltip="openChatLabel"
          class="ml-9"
          color="ghost"
          name="chat"
          :icon="true"
          @click="openChat({userId: item.userId})"
        />
      </template>
      <template v-slot:item.username="{ item }">
        <div v-tooltip="item.username">
          {{ item.username }}
        </div>
      </template>
      <template
        v-slot:item.status="{ item }"
        class="status-view"
      >
        <StIcon
          icon-name="circle"
          :color="variationIconColor(item.status)"
        />
        {{ translateStatus(item.status) }}
      </template>
      <template v-slot:item.lastStatusChange="{ item }">
        <div v-tooltip="item.lastStatusChange">
          {{ formatDate(item.lastStatusChange) }}
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import GenericChart from '@/components/cards/models/GenericChart';
import { mapActions, mapGetters, mapState } from 'vuex';
import EventBus from '@/utils/event-bus';
import { convertTimeToBrazilianFormatDate } from '@/utils/date';
import MultiSelectFilter from '@/components/filter/MultiSelectFilter.vue';
import { DsButton } from '@ds/components';
import { saveAs } from 'file-saver';
import XLSX from 'xlsx';
import sacTypeModel from '@/mixins/sac-type-model';
import channelModel from '@/mixins/channel-model';
import { getWarroomURL, getSmartCareURL } from '@/utils/url.js';
import WarRoomService from '@/services/WarRoomService';
import colors from '@/utils/colors';
import StIcon from '@stilingue/st-icon';
import repliersModel from '@/mixins/repliers-model';

export default {
  name: 'OperatorsStatus',
  components: {
    GenericChart,
    MultiSelectFilter,
    DsButton,
    StIcon,
  },
  mixins: [
    sacTypeModel,
    channelModel,
    repliersModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      headers: [
        {
          text: this.$t('components.StatusTable.user'),
          value: 'username',
        },
        {
          text: this.$t('components.StatusTable.status'),
          value: 'status',
        },
        {
          text: this.$t('components.StatusTable.lastStatus'),
          value: 'lastStatusChange',
        },
        {
          text: this.$t('components.StatusTable.viewChat'),
          value: 'userId',
          sortable: false,
        },
      ],
      options: {
        itemsPerPage: 5,
        page: 1,
      },
      footerProps: {
        'items-per-page-options': [5, 10, 15, 20, 30],
      },
      items: [],
      exportedItems: [],
      requestStatus: 'loading',
      dialog: false,
      filterDialog: false,
      hasFilter: true,
      selectedOperators: null,
      selectedStatus: null,
      filterLabel: this.$t('components.StatusTable.filterLabel'),
      trashLabel: this.$t('components.StatusTable.trashLabel'),
      openChatLabel: this.$t('components.StatusTable.openOperatorLabel'),
      closeLabel: this.$t('components.StatusTable.closeLabel'),
      operatorsLabel: this.$t('components.StatusTable.operators'),
      statusLabel: this.$t('components.StatusTable.status'),
      mapKeys: {
        user_name: this.$t('components.StatusTable.username'),
        status: this.$t('components.StatusTable.status'),
        last_status_change: this.$t('components.StatusTable.lastStatus'),
      },
      componentFilter: null,
      status: [
        {
          text: this.$t('components.StatusTable.onlineText'),
          value: 'ONLINE',
        },
        {
          text: this.$t('components.StatusTable.offlineText'),
          value: 'OFFLINE',
        },
      ],
    };
  },
  computed: {
    ...mapGetters('dashboard', [
      'activeDashboardFilters',
      'activeDashboard',
    ]),
    ...mapGetters('filter', [
      'formattedDateRange',
    ]),
    ...mapState('filter', [
      'accountId',
      'universeId',
    ]),
    ...mapState('dashboard', [
      'isStaging',
    ]),
    inputLoading() {
      return this.requestStatus === 'loading';
    },
    getOptions() {
      return this.activeDashboardFilters?.repliers?.map(res => {
        const user = {
          text: res.name,
          value: String(res.id),
        };
        return user;
      });
    },
  },
  watch: {
    options: {
      handler() {
        this.getData();
      },
      deep: true,
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getSeStudioEndpointService',
      'getWarroomService',
    ]),
    variationIconColor(status) {
      const { positiveColor, ui } = colors;

      if (status === this.status[0].value) {
        return positiveColor;
      }
      return ui.steel;
    },
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }
      this.applyFilter(options);
    },
    async getFilterId() {
      const sacRepliers = this.selectedOperators ? this.selectedOperators.value : null;
      const newCardFilter = {
        sac_replier: {
          value: sacRepliers,
        },
        is_sac: true,
      };
      return WarRoomService.encodeFilter({
        account_id: this.accountId,
        is_staging: this.isStaging,
        filter: JSON.stringify(newCardFilter),
      });
    },
    getStatus() {
      return this.selectedStatus ? this.selectedStatus.value.join(',') : null;
    },
    async getExportData() {
      this.componentFilter = await this.getFilterId();
      this.exportedItems = [];
      this.getSeStudioEndpointService({
        itemId: this.itemId,
        servicePath: 'getStatusLogs',
        serviceParams: {
          filterId: this.componentFilter,
          status: this.getStatus(),
        },
      })
        .then(response => {
          this.exportGraph(response.users);
        })
        .catch((e) => {
          console.error(e);
          this.requestStatus = 'error';
        });
    },
    async applyFilter(options) {
      this.items = [];
      this.componentFilter = await this.getFilterId();
      this.filterDialog = false;
      this.getSeStudioEndpointService({
        itemId: this.itemId,
        servicePath: 'statusTable',
        serviceParams: {
          filterId: this.componentFilter,
          status: this.getStatus(),
        },
      })
        .then((response) => {
          this.items = response.users;
          if (options?.silentRefresh) {
            EventBus.$emit('updated', { itemId: this.itemId });
          }
          this.requestStatus = 'success';
        })
        .catch((e) => {
          console.error(e);
          this.requestStatus = 'error';
        });
    },
    resetFilter() {
      this.selectedOperators = null;
      this.selectedStatus = null;
      this.applyFilter();
    },
    translateStatus(status) {
      return status === this.status[0].value
        ? this.$t('components.StatusTable.onlineText')
        : this.$t('components.StatusTable.offlineText');
    },
    exportGraph(items) {
      const translatedData = this.changeKeys(this.mapKeys, items);
      const ws = XLSX.utils.json_to_sheet(translatedData);
      const wb = { Sheets: { 'data': ws }, SheetNames: ['data'] };
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
      });
      saveAs(blob, 'status-table.xlsx');
    },
    changeKeys(map, data) {
      const response = [];
      data.forEach(item => {
        const newItem = {};
        Object.entries(map).forEach(([key, value]) => {
          newItem[value] = item[key];
        });
        response.push(newItem);
      });
      return response;
    },
    openDialog() {
      this.dialog = true;
    },
    openFilter() {
      this.filterDialog = true;
    },
    async openChat({ userId }) {
      const newCardFilter = {
        sac_replier: {
          value: [
            String(userId),
          ],
        },
        is_sac: true,
      };
      const filterId = await WarRoomService.encodeFilter({
        account_id: this.accountId,
        is_staging: this.isStaging,
        filter: JSON.stringify(newCardFilter),
      });
      const dateRange = this.formattedDateRange;
      const activatePanelURL = getWarroomURL(this.isStaging, this.accountId, this.universeId);
      const smartCareURL = getSmartCareURL(this.isStaging, dateRange, filterId);
      window.open(activatePanelURL, '_blank').location.replace(smartCareURL);
    },
    formatDate(date) {
      if (!date) {
        return;
      }
      return convertTimeToBrazilianFormatDate(date);
    },
  },
};
</script>

<style lang="scss" scoped>
.operators-status {
  &__internal-style {
    width: 100%;
    padding: 1rem;
    display: flex;
  }

  &__buttons {
    display: flex;
  }

  &__button {
    box-shadow: none;
    border-radius: unset;
  }

  &__close-button {
    display: flex;
    flex: end;
    justify-content: flex-end;
  }
}

::v-deep {
  .operators-status {
    &__table-truncate {
      td {
        max-width: 10vw;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    &__modal-style {
      * {
        color: var(--ds-color-snow) !important;
      }

      h3 {
        font-weight: var(--ds-font-weight-bold);
      }
    }

    &__v-dialog {
      overflow-x: hidden;
    }
  }
}
</style>
